import React, { useState } from 'react';
import Icon from '../AppIcon';
import Input from '../ui/Input';

const CDTCodeLookup = ({ onSelect, selectedCodes = [], multiple = false }) => {
  const [searchTerm, setSearchTerm] = useState('');
  const [isOpen, setIsOpen] = useState(false);

  // Comprehensive CDT (Current Dental Terminology) code database
  const cdtCodes = [
    { code: 'D0120', description: 'Periodic oral evaluation', category: 'Diagnostic' },
    { code: 'D0140', description: 'Limited oral evaluation - problem focused', category: 'Diagnostic' },
    { code: 'D0150', description: 'Comprehensive oral evaluation', category: 'Diagnostic' },
    { code: 'D0210', description: 'Intraoral - complete series of radiographic images', category: 'Diagnostic' },
    { code: 'D0220', description: 'Intraoral - periapical first radiographic image', category: 'Diagnostic' },
    { code: 'D0230', description: 'Intraoral - periapical each additional radiographic image', category: 'Diagnostic' },
    { code: 'D0270', description: 'Bitewing - single radiographic image', category: 'Diagnostic' },
    { code: 'D0272', description: 'Bitewings - two radiographic images', category: 'Diagnostic' },
    { code: 'D0274', description: 'Bitewings - four radiographic images', category: 'Diagnostic' },
    { code: 'D0330', description: 'Panoramic radiographic image', category: 'Diagnostic' },
    { code: 'D1110', description: 'Prophylaxis - adult', category: 'Preventive' },
    { code: 'D1120', description: 'Prophylaxis - child', category: 'Preventive' },
    { code: 'D1206', description: 'Topical application of fluoride varnish', category: 'Preventive' },
    { code: 'D1208', description: 'Topical application of fluoride', category: 'Preventive' },
    { code: 'D1351', description: 'Sealant - per tooth', category: 'Preventive' },
    { code: 'D2140', description: 'Amalgam - one surface, primary or permanent', category: 'Restorative' },
    { code: 'D2150', description: 'Amalgam - two surfaces, primary or permanent', category: 'Restorative' },
    { code: 'D2160', description: 'Amalgam - three surfaces, primary or permanent', category: 'Restorative' },
    { code: 'D2161', description: 'Amalgam - four or more surfaces, primary or permanent', category: 'Restorative' },
    { code: 'D2330', description: 'Resin-based composite - one surface, anterior', category: 'Restorative' },
    { code: 'D2331', description: 'Resin-based composite - two surfaces, anterior', category: 'Restorative' },
    { code: 'D2332', description: 'Resin-based composite - three surfaces, anterior', category: 'Restorative' },
    { code: 'D2391', description: 'Resin-based composite - one surface, posterior', category: 'Restorative' },
    { code: 'D2392', description: 'Resin-based composite - two surfaces, posterior', category: 'Restorative' },
    { code: 'D2393', description: 'Resin-based composite - three surfaces, posterior', category: 'Restorative' },
    { code: 'D2740', description: 'Crown - porcelain/ceramic substrate', category: 'Restorative' },
    { code: 'D2750', description: 'Crown - porcelain fused to high noble metal', category: 'Restorative' },
    { code: 'D2751', description: 'Crown - porcelain fused to predominantly base metal', category: 'Restorative' },
    { code: 'D2790', description: 'Crown - full cast high noble metal', category: 'Restorative' },
    { code: 'D2791', description: 'Crown - full cast predominantly base metal', category: 'Restorative' },
    { code: 'D2920', description: 'Re-cement or re-bond crown', category: 'Restorative' },
    { code: 'D3310', description: 'Endodontic therapy, anterior tooth', category: 'Endodontics' },
    { code: 'D3320', description: 'Endodontic therapy, premolar tooth', category: 'Endodontics' },
    { code: 'D3330', description: 'Endodontic therapy, molar tooth', category: 'Endodontics' },
    { code: 'D4210', description: 'Gingivectomy or gingivoplasty - four or more contiguous teeth', category: 'Periodontics' },
    { code: 'D4341', description: 'Periodontal scaling and root planing - four or more teeth per quadrant', category: 'Periodontics' },
    { code: 'D4342', description: 'Periodontal scaling and root planing - one to three teeth per quadrant', category: 'Periodontics' },
    { code: 'D4910', description: 'Periodontal maintenance', category: 'Periodontics' },
    { code: 'D5110', description: 'Complete denture - upper', category: 'Prosthodontics' },
    { code: 'D5120', description: 'Complete denture - lower', category: 'Prosthodontics' },
    { code: 'D5213', description: 'Partial denture - upper - cast metal framework with resin denture bases', category: 'Prosthodontics' },
    { code: 'D5214', description: 'Partial denture - lower - cast metal framework with resin denture bases', category: 'Prosthodontics' },
    { code: 'D6010', description: 'Surgical placement of implant body: endosteal implant', category: 'Implant Services' },
    { code: 'D6058', description: 'Abutment supported porcelain/ceramic crown', category: 'Implant Services' },
    { code: 'D6059', description: 'Abutment supported porcelain fused to metal crown', category: 'Implant Services' },
    { code: 'D7140', description: 'Extraction, erupted tooth or exposed root', category: 'Oral Surgery' },
    { code: 'D7210', description: 'Extraction, erupted tooth requiring removal of bone and/or sectioning of tooth', category: 'Oral Surgery' },
    { code: 'D7220', description: 'Removal of impacted tooth - soft tissue', category: 'Oral Surgery' },
    { code: 'D7230', description: 'Removal of impacted tooth - partially bony', category: 'Oral Surgery' },
    { code: 'D7240', description: 'Removal of impacted tooth - completely bony', category: 'Oral Surgery' },
    { code: 'D8080', description: 'Comprehensive orthodontic treatment of the adolescent dentition', category: 'Orthodontics' },
    { code: 'D8090', description: 'Comprehensive orthodontic treatment of the adult dentition', category: 'Orthodontics' },
    { code: 'D9110', description: 'Palliative (emergency) treatment of dental pain', category: 'Adjunctive General Services' },
    { code: 'D9220', description: 'Deep sedation/general anesthesia - first 15 minutes', category: 'Adjunctive General Services' },
    { code: 'D9230', description: 'Analgesia, anxiolysis, inhalation of nitrous oxide', category: 'Adjunctive General Services' },
    { code: 'D9310', description: 'Consultation - diagnostic service', category: 'Adjunctive General Services' },
    { code: 'D9430', description: 'Office visit for observation', category: 'Adjunctive General Services' }
  ];

  const filteredCodes = searchTerm
    ? cdtCodes?.filter(cdt => 
        cdt?.code?.toLowerCase()?.includes(searchTerm?.toLowerCase()) ||
        cdt?.description?.toLowerCase()?.includes(searchTerm?.toLowerCase()) ||
        cdt?.category?.toLowerCase()?.includes(searchTerm?.toLowerCase())
      )
    : cdtCodes;

  const handleSelect = (cdt) => {
    if (multiple) {
      const isSelected = selectedCodes?.some(c => c?.code === cdt?.code);
      if (isSelected) {
        onSelect?.(selectedCodes?.filter(c => c?.code !== cdt?.code));
      } else {
        onSelect?.([...selectedCodes, cdt]);
      }
    } else {
      onSelect?.(cdt);
      setIsOpen(false);
      setSearchTerm('');
    }
  };

  const isSelected = (code) => {
    if (multiple) {
      return selectedCodes?.some(c => c?.code === code);
    }
    return selectedCodes?.code === code;
  };

  return (
    <div className="relative">
      <div className="relative">
        <Input
          type="text"
          placeholder="Search CDT codes..."
          value={searchTerm}
          onChange={(e) => {
            setSearchTerm(e?.target?.value);
            setIsOpen(true);
          }}
          onFocus={() => setIsOpen(true)}
          className="pr-10"
        />
        <div className="absolute right-3 top-1/2 -translate-y-1/2 flex items-center gap-2">
          {searchTerm && (
            <button
              onClick={() => {
                setSearchTerm('');
                setIsOpen(false);
              }}
              className="text-muted-foreground hover:text-foreground transition-smooth"
            >
              <Icon name="X" size={16} />
            </button>
          )}
          <Icon name="Search" size={16} className="text-muted-foreground" />
        </div>
      </div>

      {isOpen && (
        <>
          <div 
            className="fixed inset-0 z-40" 
            onClick={() => setIsOpen(false)}
          />
          <div className="absolute z-50 w-full mt-2 bg-card border border-border rounded-lg shadow-elevation-3 max-h-96 overflow-y-auto">
            {filteredCodes?.length === 0 ? (
              <div className="p-4 text-center text-muted-foreground">
                <Icon name="Search" size={32} className="mx-auto mb-2" />
                <p className="text-sm">No CDT codes found</p>
              </div>
            ) : (
              <div className="py-2">
                {filteredCodes?.map((cdt) => (
                  <button
                    key={cdt?.code}
                    onClick={() => handleSelect(cdt)}
                    className={`w-full text-left px-4 py-3 hover:bg-muted transition-smooth border-l-4 ${
                      isSelected(cdt?.code) 
                        ? 'border-primary bg-primary/5' :'border-transparent'
                    }`}
                  >
                    <div className="flex items-start justify-between gap-3">
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2 mb-1">
                          <span className="font-mono font-semibold text-sm text-foreground">
                            {cdt?.code}
                          </span>
                          <span className="text-xs px-2 py-0.5 bg-muted rounded text-muted-foreground">
                            {cdt?.category}
                          </span>
                        </div>
                        <p className="text-sm text-muted-foreground line-clamp-2">
                          {cdt?.description}
                        </p>
                      </div>
                      {isSelected(cdt?.code) && (
                        <Icon name="Check" size={16} className="text-primary flex-shrink-0 mt-1" />
                      )}
                    </div>
                  </button>
                ))}
              </div>
            )}
          </div>
        </>
      )}
    </div>
  );
};

export default CDTCodeLookup;